import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Alert, Button, Checkbox, Descriptions, Divider, Drawer, Empty, Input, List, Modal, Space, Spin, Tag, Typography,
    message,
} from "antd";
import dayjs from "dayjs";

import {
    cancelContract, confirmDelivery, contestDelivery, getContract, signContract,
} from "../../../servics/marketplace/presaleContractApi";
import {
    ACTIVE_CONTRACT_PAYMENT, CANCELLABLE_STATUSES, CONTRACT_PAYMENT_STATUS, CONTRACT_STATUS, ESCROW_KIND, ESCROW_PURPOSE,
    ESCROW_STATUS, PAYMENT_KIND, formatRate,
} from "../../../constants/presaleContract";
import { apiErrorMessage } from "../../../constants/marketplaceCheckout";
import { ENTREPRISE_ADMIN_URL } from "../../../config/entreprisePage";
import { formatMoney } from "../../../utils/money";
import ContractPaymentModal from "./ContractPaymentModal";

const { Text, Paragraph } = Typography;

/** Suivi d'un paiement en cours (Mobile Money confirmé sur le téléphone, retour de la page carte). */
export const PAYMENT_POLL_MS = 5000;

const ENTREPRISE_PRESALE = `${ENTREPRISE_ADMIN_URL}/cultivation-intentions?tab=presale`;

const formatDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "—");
const formatDateTime = (value) => (value ? dayjs(value).format("DD/MM/YYYY HH:mm") : null);
const quantity = (value, unit) =>
    `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 }).format(Number(value))} ${unit || ""}`.trim();
const present = (value) => value !== null && value !== undefined;

/**
 * Contrat de prévente vu par l'acheteur : conditions figées, texte et empreinte, signatures,
 * paiements et ordres du séquestre. Actions selon l'étape : signer (sur l'empreinte du texte
 * affiché), annuler tant que l'avance n'est pas payée, payer l'avance puis le solde, confirmer
 * ou contester la livraison déclarée par le producteur.
 *
 * `country` est le pays du producteur, où le contrat est enregistré.
 */
const BuyerContractDrawer = ({ contractId, country, open, onClose, onChanged }) => {
    const [state, setState] = useState({ status: "idle" });
    const [read, setRead] = useState(false);
    const [busy, setBusy] = useState(null);
    const [reasonFor, setReasonFor] = useState(null);
    const [reason, setReason] = useState("");
    const [paying, setPaying] = useState(false);
    const onChangedRef = useRef(onChanged);
    onChangedRef.current = onChanged;

    const load = useCallback(async ({ quiet = false } = {}) => {
        if (!contractId || !country) return;
        if (!quiet) setState({ status: "loading" });
        try {
            setState({ status: "success", detail: await getContract(country, contractId) });
        } catch (error) {
            if (quiet) return;
            const notFound = error?.response?.status === 404;
            setState({
                status: "error",
                error: notFound
                    ? "Contrat introuvable dans ce pays. Choisissez le pays du producteur en haut de page."
                    : apiErrorMessage(error, "Impossible de charger le contrat."),
            });
        }
    }, [contractId, country]);

    useEffect(() => {
        if (open) {
            setRead(false);
            load();
        }
    }, [open, load]);

    const detail = state.detail;
    const contract = detail?.contract;
    const paymentInFlight = (detail?.payments || []).some((payment) => ACTIVE_CONTRACT_PAYMENT.includes(payment.status));

    // Le backend synchronise le paiement avec l'orchestrateur à chaque lecture.
    useEffect(() => {
        if (!open || !paymentInFlight) return undefined;
        const timer = setInterval(async () => {
            await load({ quiet: true });
            onChangedRef.current?.();
        }, PAYMENT_POLL_MS);
        return () => clearInterval(timer);
    }, [open, paymentInFlight, load]);

    const run = async (key, action, success) => {
        setBusy(key);
        try {
            await action();
            message.success(success);
            await load();
            onChanged?.();
            return true;
        } catch (error) {
            message.error(apiErrorMessage(error, "L'opération n'a pas pu être enregistrée."));
            return false;
        } finally {
            setBusy(null);
        }
    };

    const submitReason = async () => {
        const text = reason.trim();
        const done = reasonFor === "cancel"
            ? await run("cancel", () => cancelContract(country, contract.id, text), "Contrat annulé")
            : await run("contest", () => contestDelivery(country, contract.id, text),
                "Livraison contestée : le producteur doit la déclarer à nouveau");
        if (done) {
            setReasonFor(null);
            setReason("");
        }
    };

    const money = (amount) => formatMoney(amount, contract.currency?.code);
    const status = contract ? CONTRACT_STATUS[contract.status] || { label: contract.status, color: "default" } : null;
    const isProducer = contract?.viewerSide === "PRODUCER";
    const due = contract && Number(contract.amountDue) > 0
        && ["AWAITING_ADVANCE", "AWAITING_BALANCE"].includes(contract.status);

    const actions = () => {
        if (!contract || isProducer) return null;
        const blocks = [];
        if (contract.status === "AWAITING_SIGNATURES" && !contract.buyerSignature) {
            blocks.push(
                <Space key="sign" orientation="vertical" style={{ width: "100%" }}>
                    <Checkbox checked={read} onChange={(e) => setRead(e.target.checked)}>
                        J&apos;ai lu le contrat ci-dessous et j&apos;en accepte les conditions.
                    </Checkbox>
                    <Button type="primary" disabled={!read} loading={busy === "sign"}
                            onClick={() => run("sign", () => signContract(country, contract.id, detail.termsSha256),
                                "Contrat signé")}>
                        Signer le contrat
                    </Button>
                </Space>
            );
        }
        if (due) {
            blocks.push(
                <Button key="pay" type="primary" disabled={paymentInFlight} onClick={() => setPaying(true)}>
                    {contract.status === "AWAITING_ADVANCE" ? "Payer l'avance" : "Payer le solde"} ({money(contract.amountDue)})
                </Button>
            );
        }
        if (contract.status === "DELIVERY_DECLARED") {
            blocks.push(
                <Space key="delivery" wrap>
                    <Button type="primary" loading={busy === "confirm"}
                            onClick={() => run("confirm", () => confirmDelivery(country, contract.id),
                                "Livraison confirmée")}>
                        Confirmer la livraison
                    </Button>
                    <Button onClick={() => setReasonFor("contest")}>Contester</Button>
                </Space>
            );
        }
        if (CANCELLABLE_STATUSES.includes(contract.status) && !paymentInFlight) {
            blocks.push(<Button key="cancel" danger onClick={() => setReasonFor("cancel")}>Annuler le contrat</Button>);
        }
        return blocks.length ? <Space orientation="vertical" style={{ width: "100%" }}>{blocks}</Space> : null;
    };

    const notice = () => {
        if (isProducer) {
            return {
                type: "info",
                title: "Vous êtes le producteur de ce contrat : il se gère depuis votre espace entreprise.",
                action: <Button size="small" href={`${ENTREPRISE_PRESALE}&contract=${contract.id}`}>Ouvrir</Button>,
            };
        }
        if (paymentInFlight) {
            return { type: "info", title: "Paiement en cours : confirmez-le sur votre téléphone ou sur la page du prestataire. Le contrat se met à jour tout seul." };
        }
        switch (contract.status) {
            case "AWAITING_SIGNATURES":
                return contract.buyerSignature
                    ? { type: "info", title: "Vous avez signé : le contrat attend la signature du producteur." }
                    : null;
            case "AWAITING_ADVANCE":
                return { type: "warning", title: "Signé par les deux parties : payez l'avance pour lancer la production." };
            case "IN_PRODUCTION":
                return { type: "success", title: "Avance reçue : le producteur cultive votre commande. Il déclarera la livraison." };
            case "DELIVERY_DECLARED":
                return { type: "warning", title: "Le producteur a déclaré la livraison : confirmez la quantité reçue ou contestez-la." };
            case "AWAITING_BALANCE":
                return { type: "warning", title: "Livraison confirmée : payez le solde pour clore le contrat." };
            case "COMPLETED":
                return { type: "success", title: "Contrat terminé." };
            default:
                return null;
        }
    };

    const banner = contract ? notice() : null;
    const refunds = (detail?.escrowOrders || []).filter((order) => order.kind === "REFUND_BUYER");

    return (
        <Drawer open={open} onClose={onClose} size={720} destroyOnHidden
                title={contract
                    ? <Space wrap>{`Contrat ${contract.reference}`}<Tag color={status.color}>{status.label}</Tag></Space>
                    : "Contrat de prévente"}>
            {state.status === "loading" && <Spin />}
            {state.status === "error" && <Alert type="error" showIcon title={state.error} />}
            {contract && (
                <>
                    {banner && <Alert showIcon style={{ marginBottom: 16 }} {...banner} />}
                    {actions()}
                    <Divider />
                    <Descriptions column={1} size="small" bordered>
                        <Descriptions.Item label="Producteur">{contract.producerCompanyName}</Descriptions.Item>
                        <Descriptions.Item label="Acheteur">
                            {contract.buyerCompanyName ? `${contract.buyerCompanyName} — ${contract.buyerFullName}` : contract.buyerFullName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Produit">
                            {contract.varietyName ? `${contract.productName} — ${contract.varietyName}` : contract.productName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Quantité">{quantity(contract.quantity, contract.measurementUnit)}</Descriptions.Item>
                        <Descriptions.Item label="Prix unitaire">
                            {money(contract.unitPrice)} / {contract.measurementUnit}
                        </Descriptions.Item>
                        <Descriptions.Item label="Montant total">{money(contract.totalAmount)}</Descriptions.Item>
                        <Descriptions.Item label="Avance à la signature">
                            {money(contract.advanceAmount)} ({formatRate(Number(contract.advancePercent) / 100)})
                        </Descriptions.Item>
                        <Descriptions.Item label="Commission ASSIGANTO">
                            {formatRate(contract.commissionRate)} de la valeur livrée, payée par le producteur
                        </Descriptions.Item>
                        <Descriptions.Item label="Récolte prévue">{formatDate(contract.expectedHarvestDate)}</Descriptions.Item>
                        {contract.deliveryDate && (
                            <Descriptions.Item label="Livraison souhaitée">{formatDate(contract.deliveryDate)}</Descriptions.Item>
                        )}
                        <Descriptions.Item label="Signature du producteur">
                            {formatDateTime(contract.producerSignature?.signedAt) || <Text type="secondary">En attente</Text>}
                        </Descriptions.Item>
                        <Descriptions.Item label="Votre signature">
                            {formatDateTime(contract.buyerSignature?.signedAt) || <Text type="secondary">En attente</Text>}
                        </Descriptions.Item>
                        {present(contract.deliveredQuantity) && (
                            <Descriptions.Item label="Quantité livrée">
                                {quantity(contract.deliveredQuantity, contract.measurementUnit)}
                                {contract.deliveryConfirmedAt ? " (confirmée)" : " (à confirmer)"}
                            </Descriptions.Item>
                        )}
                        {present(contract.deliveredAmount) && (
                            <Descriptions.Item label="Règlement">
                                Valeur livrée {money(contract.deliveredAmount)}
                                {" · "}solde {money(contract.balanceAmount)}
                                {Number(contract.refundAmount) > 0 && ` · remboursement ${money(contract.refundAmount)}`}
                            </Descriptions.Item>
                        )}
                        {contract.cancelledAt && (
                            <Descriptions.Item label="Annulé le">
                                {formatDateTime(contract.cancelledAt)}
                                {contract.cancellationReason && ` — ${contract.cancellationReason}`}
                            </Descriptions.Item>
                        )}
                    </Descriptions>

                    <Divider titlePlacement="start">Vos paiements</Divider>
                    <List size="small" dataSource={detail.payments}
                          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Aucun paiement" /> }}
                          renderItem={(payment) => {
                              const paymentStatus = CONTRACT_PAYMENT_STATUS[payment.status]
                                  || { label: payment.status, color: "default" };
                              return (
                                  <List.Item>
                                      <Space wrap>
                                          <Text strong>{PAYMENT_KIND[payment.kind] || payment.kind}</Text>
                                          {formatMoney(payment.amount, payment.currency)}
                                          <Tag color={paymentStatus.color}>{paymentStatus.label}</Tag>
                                          {payment.phoneMasked && <Text type="secondary">{payment.phoneMasked}</Text>}
                                          <Text type="secondary">{formatDateTime(payment.succeededAt || payment.createdAt)}</Text>
                                          {payment.failureReason && <Text type="danger">{payment.failureReason}</Text>}
                                          {payment.nextActionMessage && ACTIVE_CONTRACT_PAYMENT.includes(payment.status) && (
                                              <Text type="secondary">{payment.nextActionMessage}</Text>
                                          )}
                                      </Space>
                                  </List.Item>
                              );
                          }} />

                    {refunds.length > 0 && (
                        <>
                            <Divider titlePlacement="start">Remboursements</Divider>
                            <List size="small" dataSource={refunds} renderItem={(order) => {
                                const orderStatus = ESCROW_STATUS[order.status] || { label: order.status, color: "default" };
                                return (
                                    <List.Item>
                                        <Space wrap>
                                            <Text strong>{ESCROW_KIND[order.kind]}</Text>
                                            <span>({ESCROW_PURPOSE[order.purpose] || order.purpose})</span>
                                            {formatMoney(order.amount, order.currency)}
                                            <Tag color={orderStatus.color}>{orderStatus.label}</Tag>
                                            {order.executionReference && <Text type="secondary">Réf. {order.executionReference}</Text>}
                                        </Space>
                                    </List.Item>
                                );
                            }} />
                        </>
                    )}

                    <Divider titlePlacement="start">Texte du contrat</Divider>
                    <Paragraph style={{ whiteSpace: "pre-wrap", maxHeight: 420, overflowY: "auto", fontSize: 13,
                        background: "#fafafa", padding: 12, border: "1px solid #f0f0f0" }}>
                        {detail.termsText}
                    </Paragraph>
                    <Text type="secondary" style={{ fontSize: 12, wordBreak: "break-all" }}>
                        Empreinte SHA-256 : {detail.termsSha256}
                    </Text>
                </>
            )}

            <Modal open={!!reasonFor}
                   title={reasonFor === "cancel" ? "Annuler ce contrat ?" : "Contester la livraison ?"}
                   okText={reasonFor === "cancel" ? "Annuler le contrat" : "Contester"} cancelText="Retour"
                   okButtonProps={{ danger: true }} confirmLoading={busy === "cancel" || busy === "contest"}
                   onCancel={() => setReasonFor(null)} onOk={submitReason}>
                <Input.TextArea rows={3} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)}
                                placeholder="Motif transmis au producteur (facultatif)" aria-label="Motif" />
            </Modal>

            <ContractPaymentModal open={paying} country={country} contract={contract}
                                  onClose={() => setPaying(false)}
                                  onStarted={() => {
                                      setPaying(false);
                                      message.info("Paiement lancé : confirmez-le sur votre téléphone.");
                                      load({ quiet: true });
                                      onChanged?.();
                                  }} />
        </Drawer>
    );
};

export default BuyerContractDrawer;
